import React from "react";
import { motion } from "framer-motion";
import { FaLeaf, FaShieldAlt, FaRecycle, FaSmile } from "react-icons/fa";
import { GiPlantRoots } from "react-icons/gi";
import { MdMedicalServices } from "react-icons/md";

const Benifits = () => {
  const benefits = [
    {
      icon: <FaLeaf className="text-green-400 text-4xl" />,
      title: "Eco-Friendly",
      desc: "Sustainable packaging that cares for our planet",
    },
    {
      icon: <GiPlantRoots className="text-green-400 text-4xl" />,
      title: "Natural Ingredients",
      desc: "Made with pure, plant-based ingredients",
    },
    {
      icon: <FaShieldAlt className="text-green-400 text-4xl" />,
      title: "Safe Formula",
      desc: "SLS-free and gentle on young teeth",
    },
    {
      icon: <FaSmile className="text-green-400 text-4xl" />,
      title: "Kid-Loved Flavors",
      desc: "Delicious tastes children actually enjoy",
    },
    {
      icon: <MdMedicalServices className="text-green-400 text-4xl" />,
      title: "Dentist Recommended",
      desc: "Trusted by dental professionals",
    },
    {
      icon: <FaRecycle className="text-green-400 text-4xl" />,
      title: "Recyclable Packaging",
      desc: "Responsible packaging choices",
    },
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const iconAnimation = {
    hidden: { scale: 0, rotate: -180 },
    visible: { 
      scale: 1, 
      rotate: 0,
      transition: { 
        type: "spring", 
        stiffness: 200,
        damping: 15 
      }
    }
  };

  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-6 lg:px-16 text-center">
        <motion.h2 
          className="text-3xl md:text-4xl font-extrabold text-[#0b1b33] mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeInUp}
        >
          Discover the benefits that set our toothpaste apart
        </motion.h2>

        {/* Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              whileHover={{ 
                y: -5,
                scale: 1.02,
                transition: { type: "spring", stiffness: 400, damping: 25 }
              }}
              className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-8 flex flex-col items-center text-center cursor-default group"
            >
              {/* Icon Container */}
              <motion.div 
                className="bg-green-50 p-4 rounded-full mb-4 flex items-center justify-center group-hover:bg-green-100 transition-colors duration-300"
                variants={iconAnimation}
                whileHover={{ 
                  scale: 1.1,
                  rotate: 5,
                  transition: { type: "spring", stiffness: 300 }
                }}
              >
                {benefit.icon}
              </motion.div>
              
              <motion.h3 
                className="text-lg font-semibold text-[#0b1b33] mb-2"
                whileHover={{ color: "#059669" }}
                transition={{ duration: 0.2 }}
              >
                {benefit.title}
              </motion.h3>
              
              <motion.p 
                className="text-gray-500 text-sm"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
              >
                {benefit.desc}
              </motion.p>

              {/* Hover underline effect */}
              <motion.div 
                className="w-0 h-0.5 bg-green-400 mt-3 group-hover:w-8 transition-all duration-300"
                initial={{ width: 0 }}
                whileHover={{ width: 32 }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Benifits;